import { useMemo } from "react";
import { startOfDay, addDays, subDays, eachDayOfInterval, format } from "date-fns";

/** Number of past days to show */
const DAYS_PAST = 2;
/** Number of future days to show (including today) */
const DAYS_FUTURE = 12;
/** Total visible days */
export const VISIBLE_DAYS = DAYS_PAST + DAYS_FUTURE; // 14
/** Total minutes in the visible window */
export const TOTAL_VISIBLE_MINUTES = VISIBLE_DAYS * 24 * 60;

/**
 * Returns the default rolling 14-day window: 2 days ago through 12 days from now.
 */
export function getDefaultScheduleRange(): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  return {
    from: subDays(today, DAYS_PAST),
    to: addDays(today, DAYS_FUTURE - 1),
  };
}

interface UseWeekNavigationResult {
  /** Start of the visible window (2 days ago at midnight) */
  weekStart: Date;
  /** End of the visible window (exclusive) */
  weekEnd: Date;
  /** Array of days in the visible window */
  weekDays: Date[];
  /** Formatted range string */
  formatWeekRange: () => string;
}

/**
 * Hook providing a rolling 14-day window: 2 days in the past + 12 days in the future.
 * No navigation needed — the window is always anchored to today.
 */
export interface WeekNavigationOverride {
  from: Date;
  to: Date;
}

/**
 * @param override Optional explicit date range. When provided, replaces the
 * rolling 14-day window with the inclusive [from, to] range.
 */
export function useWeekNavigation(override?: WeekNavigationOverride | null): UseWeekNavigationResult {
  const today = startOfDay(new Date());
  const overrideKey = override ? `${override.from.getTime()}-${override.to.getTime()}` : "default";

  const weekStart = useMemo(
    () => (override ? startOfDay(override.from) : subDays(today, DAYS_PAST)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrideKey, today.getTime()]
  );

  const weekEnd = useMemo(
    () => (override ? addDays(startOfDay(override.to), 1) : addDays(weekStart, VISIBLE_DAYS)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrideKey, weekStart]
  );

  const weekDays = useMemo(
    () =>
      eachDayOfInterval({
        start: weekStart,
        end: override ? startOfDay(override.to) : addDays(weekStart, VISIBLE_DAYS - 1),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrideKey, weekStart]
  );

  const formatWeekRange = useMemo(
    () => () => {
      const end = override ? startOfDay(override.to) : addDays(weekStart, VISIBLE_DAYS - 1);
      return `${format(weekStart, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrideKey, weekStart]
  );

  return {
    weekStart,
    weekEnd,
    weekDays,
    formatWeekRange,
  };
}
